import { useState, useMemo } from 'react'
import { type Bot, useBotSort } from '../hooks/useBotSort'
import { BotListView } from './BotListView'
import { BotsToolbar } from './BotsToolbar'
import { EmptyState } from '~/elements'
import { AsyncButton } from '@bpinternal/ui-kit-next'
import EmptyBotsIcon from '~/assets/emptyStates/chat.svg?react'
import EmptyBotsIconDark from '~/assets/emptyStates/darkChat.svg?react'
import { Plus } from 'lucide-react'
import { BotCardView } from './BotCardView'
import { useBotCreationStore, useLocalStore } from '~/stores'
import { useNavigate } from '@tanstack/react-router'
import { useSuspenseQuery } from '@tanstack/react-query'
import { userPreferencesQueryOptions } from '~/queries/user'
import { useSetAccountPreferences } from '~/hooks/users/useSetAccountPreferences'

type BotListProps = {
  bots: Bot[]
  workspaceId: string
  userId: string
}

export const BotsView = ({ bots, workspaceId, userId }: BotListProps) => {
  const { data: viewMode } = useSuspenseQuery(
    userPreferencesQueryOptions({ path: '$workspaceId/botsViewMode', params: { workspaceId } })
  )
  const { mutate: setViewMode } = useSetAccountPreferences()
  const [filter, setFilter] = useState('')
  const { sortType, setSortType, sortedBots } = useBotSort(bots, 'lastViewed', workspaceId)
  const createBot = useBotCreationStore((state) => state.createBot)
  const theme = useLocalStore((s) => s.theme)
  const [isCreatingBot, setIsCreatingBot] = useState(false)
  const navigate = useNavigate()

  const filteredBots = useMemo(
    () => sortedBots.filter((bot) => bot.name.toLowerCase().includes(filter.toLowerCase())),
    [sortedBots, filter]
  )

  const handleCreateBot = () => {
    setIsCreatingBot(true)
    createBot({
      workspaceId,
      onBotCreated: (newBot) =>
        navigate({
          to: '/workspaces/$workspaceId/bots/$botId/new-bot',
          from: '/workspaces/$workspaceId',
          params: { botId: newBot.id },
        }),
      onBotError: () => setIsCreatingBot(false),
    })
  }

  function renderContent() {
    if (sortedBots.length === 0) {
      return (
        <EmptyState
          icon={theme === 'dark' ? EmptyBotsIconDark : EmptyBotsIcon}
          className="h-80"
          iconSize={8}
          title="Workspace has no bots"
          description="Your workspace has no bots. Get started by creating a new bot."
          primaryAction={
            <AsyncButton
              loading={isCreatingBot}
              onClick={handleCreateBot}
              className="bg-accent-9 text-white hover:bg-accent-10"
            >
              <Plus />
              Create Bot
            </AsyncButton>
          }
        />
      )
    }

    if (filteredBots.length === 0) {
      return (
        <EmptyState
          className="h-80"
          iconSize={8}
          title={`Workspace has no bots named "${filter}"`}
          description="Try a different search term."
        />
      )
    }

    return viewMode === 'list' ? (
      <BotListView bots={filteredBots} workspaceId={workspaceId} userId={userId} />
    ) : (
      <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 2xl:grid-cols-3">
        {filteredBots.map((bot) => (
          <BotCardView key={bot.id} bot={bot} workspaceId={workspaceId} userId={userId} />
        ))}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 @container">
      <BotsToolbar
        filter={filter}
        onFilterChange={setFilter}
        sortType={sortType}
        onSortTypeChange={setSortType}
        viewMode={viewMode}
        onViewModeChange={(mode) =>
          setViewMode({ path: '$workspaceId/botsViewMode', params: { workspaceId }, value: mode })
        }
      />
      {renderContent()}
    </div>
  )
}
